import type { LocalProps } from './types.js';
import {
  calculateNextBitrate,
  closestEncoderProfile,
  DefaultBitrate,
  encoderProfiles
} from '#~/video/constants.js';
import { Annotation, Column, Group } from '@studio/design-system/form';
import * as Select from '@studio/design-system/select';
import { VideoDefaults } from '@studio/models/video';
import { LocalDefaults } from '@studio/models/video/local';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { defaults } from 'lodash-es';
import type { ReactElement } from 'react';

/** `<Local>` settings. */
export const Local = ({ settings, ...props }: LocalProps): ReactElement => {
  const current = defaults({}, settings.value, LocalDefaults, VideoDefaults);

  const heightStr = String(current.query?.height ?? '1080');
  const height = Number.parseInt(heightStr, 10);

  const bitrateStr = current.query?.bitrate;
  const bitrate = bitrateStr ? Number.parseInt(String(bitrateStr), 10) : DefaultBitrate;

  const encoderProfile = closestEncoderProfile(bitrate, height);

  const handleFrameRateChange = useLatestFn((value: string) => {
    const frameRate = value;

    if (frameRate && frameRate !== current.query?.framerate) {
      const heightStr = String(current.query?.height ?? '1080');
      const height = Number.parseInt(heightStr, 10);

      const bitrate = calculateNextBitrate(encoderProfile, height);

      const query = { ...current.query, framerate: frameRate, bitrate };

      settings({ ...current, query });
    }
  });

  const handleResolutionChange = useLatestFn((resolution: string) => {
    const [width, height] = resolution.split('x').map((a) => Number.parseInt(a, 10));

    if ((width && width !== current.query?.width) || (height && height !== current.query?.height)) {
      const bitrate = height ? calculateNextBitrate(encoderProfile, height) : DefaultBitrate;

      const query = { ...current.query, width, height, bitrate };

      settings({ ...current, query });
    }
  });

  const handleBitrateChange = useLatestFn((bitrate: number) => {
    const query = { ...current.query, bitrate };

    settings({ ...current, query });
  });

  const handleEncoderProfileChange = useLatestFn((profile: string) => {
    const heightStr = String(current.query?.height ?? '1080');
    const height = Number.parseInt(heightStr, 10);

    const bitrate = calculateNextBitrate(profile, height);

    handleBitrateChange(bitrate);
  });

  const frameRateOptions = ['60/1', '30/1', '15/1'];

  const resolutionOptions = ['1920x1080', '1760x1328', '1552x1552', '1280x720', '640x480'];

  const currentResolution = current.query?.width
    ? `${current.query?.width as number}x${current.query?.height as number}`
    : '1920x1080';

  return (
    <Column {...props}>
      <Group annotation={<Annotation.Required />} label='Frame Rate'>
        <Select.Root
          onValueChange={handleFrameRateChange}
          value={(current.query?.framerate as T_Maybe<string>) ?? '30/1'}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>

          <Select.Content>
            {frameRateOptions.map((a) => (
              <Select.Item key={a} value={a}>
                {a}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Group>

      <Group annotation={<Annotation.Required />} label='Resolution'>
        <Select.Root onValueChange={handleResolutionChange} value={currentResolution}>
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>

          <Select.Content>
            {resolutionOptions.map((a) => (
              <Select.Item key={a} value={a}>
                {a}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Group>
      <Group label='Encoder Profile'>
        <Select.Root onValueChange={handleEncoderProfileChange} value={encoderProfile}>
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>

          <Select.Content>
            {encoderProfiles.map((a) => (
              <Select.Item key={a} value={a}>
                {a}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        {/*
        <Input
          onChange={(e) => handleBitrateChange(Number.parseInt(e, 10))}
          placeholder='Bitrate'
          type='number'
          value={String(current.query?.bitrate)}
        />*/}
      </Group>
    </Column>
  );
};
